import { Box } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CustomTextArea from "../../../../../components/CustomTextArea";
import Flex from "../../../../../components/Flex";
import Text from "../../../../../components/Text";
import CustomTabs from "../../../../../components/app/CustomTabs";
import DeleteModal from "../../../../../components/app/DeleteModal";
import useCurrentUser from "../../../../../hooks/local-storage/use-current-user";
import useDisclosure from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useAppointmentsV2 } from "../../../../../queries/patient-portal/forms/appointment/use-appointment-list-v2";
import { useUpdateAppointmentStatus } from "../../../../../queries/use-change-appointment-status";
import { DEF_PAGINATION, getXtenantId } from "../../../../../utils/utils";
import ViewEncounterSummaryDrawer from "../../../../../pages/app/provider/billings/encounter/ViewEncounterSummaryDrawer";
import PatientIntakeFormModal from "../../dashboard/actions/PatientIntakeFormModal";
import { useAppointmentContext } from "../context/AppointmentContext";
import PatientPortalRescheduleAppointmentModal from "../patientAppointmentForm/PatientPortalRescheduleAppointmentModal";
import { Appointment } from "../types/appointment.types";
import AppointmentCard from "./AppointmentCard";
import AppointmentFilters from "./AppointmentFilters";

type AppointmentActiveTab = "upcoming" | "pending" | "past";

const APPOINTMENT_TABS = [
  { value: "upcoming", label: "Upcoming Appointments" },
  { value: "past", label: "Past Appointments" },
  { value: "pending", label: "Pending Requests" },
];

const currentDate = dayjs().startOf("day").toISOString();
const nextDate = dayjs().add(1, "day").startOf("day").toISOString();
const endDateOfYear = dayjs().endOf("year").toISOString();
const startDateOfYear = dayjs().startOf("year").toISOString();

// ECRH-4336: status filters per tab — same API, different status values
const TAB_STATUS_CONFIG: Record<
  AppointmentActiveTab,
  {
    startDate: string;
    endDate: string;
    appointmentStatus?: string[];
    appStatusForPatient: string[];
  }
> = {
  upcoming: {
    startDate: currentDate,
    endDate: endDateOfYear,
    appointmentStatus: ["CONFIRMED", "PENDING", "CHECKED_IN"],
    appStatusForPatient: ["CONFIRMED", "PENDING"],
  },
  pending: {
    startDate: currentDate,
    endDate: endDateOfYear,
    appStatusForPatient: ["PENDING"],
  },
  past: {
    startDate: startDateOfYear,
    endDate: nextDate,
    appointmentStatus: ["COMPLETED"],
    appStatusForPatient: ["CONFIRMED"],
  },
};

const mapAppointmentStatus = (status?: string): Appointment["status"] => {
  switch (status) {
    case "CONFIRMED":
    case "Confirmed":
      return "Confirmed";
    case "SCHEDULED":
    case "Scheduled":
    case "PENDING":
    case "Pending":
      return "Scheduled";
    case "CANCELLED":
    case "Cancelled":
    case "Canceled":
      return "Canceled";
    case "COMPLETED":
    case "Completed":
      return "Completed";
    case "CHECKED_IN":
    case "Checked In":
      return "Checked In";
    default:
      return "Scheduled";
  }
};

const mapAppointmentMode = (mode?: string): Appointment["mode"] => {
  switch (mode) {
    case "In-Person":
    case "IN_PERSON":
    case "IN-PERSON":
    case "IN PERSON":
    case "In Person":
      return "In Person";
    default:
      return "Telehealth";
  }
};

const PatientPortalAppointmentList: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [patient] = useCurrentUser();
  const { filters } = useAppointmentContext();
  const [selectedRescheduleAppointment, setSelectedRescheduleAppointment] =
    useState<any>();
  const rescheduleDisclosure = useDisclosure(false);
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const [activeTab, setActiveTab] = useState<AppointmentActiveTab>(
    (searchParams.get("appointment") as AppointmentActiveTab) ?? "upcoming"
  );

  const tabStatusConfig = TAB_STATUS_CONFIG[activeTab];

  const appointment_mode_mapper: Record<string, string> = {
    Telehealth: "VIRTUAL",
    "In Person": "IN_PERSON",
  };

  const { data: appointmentsData } = useAppointmentsV2({
    pagination: DEF_PAGINATION,
    startDate: tabStatusConfig.startDate,
    endDate: tabStatusConfig.endDate,
    appointmentStatus: tabStatusConfig.appointmentStatus,
    appStatusForPatient: tabStatusConfig.appStatusForPatient,
    appointmentMode:
      filters?.appointmentMode === "all"
        ? undefined
        : filters?.appointmentMode
          ? [
              appointment_mode_mapper[
                filters.appointmentMode as keyof typeof appointment_mode_mapper
              ],
            ]
          : undefined,
    patientUuid: [patient?.uuid ?? ""],
    providerUuid:
      filters?.provider === "all"
        ? undefined
        : filters?.provider
          ? [filters.provider]
          : undefined,
    providerGroupId: getXtenantId(),
  });

  const rawAppointments = appointmentsData?.data?.content ?? [];
  const filteredAppointments = useMemo<Appointment[]>(() => {
    return rawAppointments.map((item) => {
      const providerName = item.providerName
        ? item.providerName
        : [item.providerFirstName, item.providerLastName]
            .filter(Boolean)
            .join(" ");

      const appointmentTime =
        item.appointmentTime ?? item.dateTime ?? currentDate;

      return {
        uuid: item.uuid ?? "",
        provider: {
          id: item.providerUuid ?? "",
          name: providerName || "Provider",
          specialty: item.providerRole || "-",
          avatar: item.providerProfilePicture || "",
        },
        dateTime: new Date(appointmentTime),
        duration: Number(item.duration ?? 0),
        mode: mapAppointmentMode(item.mode ?? item.appointmentMode),
        location: item.location ?? item.locationName,
        reason: item.chiefComplaint || item.reason || "-",
        status: mapAppointmentStatus(item.appointmentStatus ?? item.status),
        intakeComplete:
          item.intakeComplete === true || item.intakeFormStatus === "COMPLETED",
        intakeForms: item.customForms,
        patientJoinLink: item.patientJoinLink,
        appStatusForPatient: item.appStatusForPatient,
      };
    });
  }, [rawAppointments]);

  // Keep selectedAppointment for the Cancel flow (needs full object)
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment>();

  // Encounter summary drawer — same pattern as ReadyForBillingTab.
  // Opened with appointmentId via `encounterSummeryDisc.open(appointmentId)`.
  const encounterSummeryDisc = useDisclosure();

  const [reason, setReason] = useState<string>("");
  const appointmentStatusChangeDisclosure = useDisclosure(false, {
    onClose: () => setReason(""),
  });

  const handleCancel = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    appointmentStatusChangeDisclosure.open();
  };

  const handleReschedule = (appointmentId: string) => {
    const appointment = rawAppointments.find(
      (item) => item?.uuid === appointmentId
    );
    setSelectedRescheduleAppointment(appointment);
    rescheduleDisclosure.open();
  };

  const handleJoinVisit = (patientJoinLink?: string) => {
    if (!patientJoinLink) {
      showSnackbar({ type: "error", message: "Join link unavailable" });
      return;
    }

    navigate(`/app/preview?room=${patientJoinLink?.split("room=")?.[1]}`);
  };

  const intakeFormModalDisc = useDisclosure(false, {
    onClose: () => {
      client.invalidateQueries({ queryKey: ["appointment-list-v2"] });
    },
  });

  const handleCompleteIntakeForm = (appointmentId: string) => {
    const event = rawAppointments.find((item) => item?.uuid === appointmentId);
    if (!event) return;

    // ECRH-5118: guard against appointments with no assigned intake form.
    // Opening the modal without form sections crashes the intake form
    // (undefined `.map()` in the tabs), so surface a friendly message instead.
    if (!event?.customForms) {
      showSnackbar({
        type: "info",
        message: "No intake form assigned for this appointment",
      });
      return;
    }

    intakeFormModalDisc.open({
      appointmentId: event?.uuid,
      patientId: event?.patient ?? event?.patientUuid ?? patient?.uuid,
      intakeForms: event?.customForms,
    });
  };

  const handleViewSummary = (appointmentId: string) => {
    encounterSummeryDisc.open(appointmentId);
  };

  const client = useQueryClient();
  const { mutateAsync: updateStatus } = useUpdateAppointmentStatus();

  return (
    <Flex flexDirection="column" width="100%">
      <PatientPortalRescheduleAppointmentModal
        open={rescheduleDisclosure.opened}
        onClose={rescheduleDisclosure.close}
        appointmentData={selectedRescheduleAppointment}
      />

      {encounterSummeryDisc.opened && encounterSummeryDisc.data && (
        <ViewEncounterSummaryDrawer
          key={encounterSummeryDisc.data}
          disclosure={encounterSummeryDisc}
        />
      )}

      {intakeFormModalDisc.opened && (
        <PatientIntakeFormModal disclosureHost={intakeFormModalDisc} />
      )}

      <DeleteModal
        w="450px"
        open={appointmentStatusChangeDisclosure.opened}
        onClose={appointmentStatusChangeDisclosure.close}
        onConfirmation={async () => {
          if (!reason?.trim()) {
            const err = {
              response: {
                data: { message: "Reason is a required field" },
              },
            };
            return Promise.reject(err);
          }

          return updateStatus({
            reason: reason,
            appointmentId: selectedAppointment?.uuid ?? "",
            status: "CANCELLED",
            xTENANTID: getXtenantId(),
            appointmentRequest: true,
          }).then((res) => {
            appointmentStatusChangeDisclosure.close();
            client.invalidateQueries({
              queryKey: ["appointment-list-v2"],
            });

            return res;
          });
        }}
        content={
          (
            <Box>
              <Text align="center">You want to cancel this appointment.</Text>
              <Text align="center">Confirm if you have mentioned reason</Text>
            </Box>
          ) as any
        }
        input={
          <Box width={"100%"}>
            <CustomTextArea
              required
              value={reason}
              minRows={5}
              placeholder="Enter reason here..."
              onChange={(e) => {
                setReason(e.currentTarget.value);
              }}
            />
          </Box>
        }
        confirmButtonText="Confirm"
        cancelButtonText="Cancel"
      />
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <CustomTabs
          tabsData={APPOINTMENT_TABS}
          value={activeTab}
          onChange={(v) => {
            setActiveTab(v as AppointmentActiveTab);
            setSearchParams({ appointment: v as AppointmentActiveTab });
          }}
        />
        <AppointmentFilters />
      </Box>

      <Box>
        {filteredAppointments.length === 0 ? (
          <Flex
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            gap={2}
            height={"80dvh"}
          >
            <Text fontSize="16px" color="#1A1A1A99">
              No {activeTab} appointments found
            </Text>
          </Flex>
        ) : (
          <Box
            sx={{
              overflowY: "auto",
              height: "calc(100svh - 150px)",
              padding: 2,
            }}
          >
            {filteredAppointments.map((appointment) => (
              <AppointmentCard
                key={appointment.uuid}
                appointment={appointment}
                isPast={activeTab === "past"}
                isPending={activeTab === "pending"}
                onCancel={() => handleCancel(appointment)}
                onReschedule={handleReschedule}
                onJoinVisit={handleJoinVisit}
                onCompleteIntakeForm={handleCompleteIntakeForm}
                onViewSummary={handleViewSummary}
              />
            ))}
          </Box>
        )}
      </Box>
    </Flex>
  );
};

export default PatientPortalAppointmentList;
